import { useTranslation } from 'react-i18next';
import { useLocation, useMatches } from 'react-router-dom';
import { Head } from 'vite-react-ssg';

import { baseGraph, faqGraph, jsonLd } from '../../lib/structured-data';
import type { RouteMeta } from '../../routes/routes';

const SITE_NAME = 'WorkingClock';

/**
 * Canonical marketing origin (apex, no `www`). Baked as an absolute URL into
 * every page's `<link rel="canonical">` so search engines collapse the apex
 * and the (301-redirected) `www.` host onto a single indexable URL. Matches
 * MARKETING_ORIGIN in infra/deploy/deploy.sh and the sitemap.xml host.
 */
const CANONICAL_ORIGIN = 'https://workingclock.app';

/** Shared social-card image (1200×630), served from public/. */
const OG_IMAGE = `${CANONICAL_ORIGIN}/og-image.png`;

interface MatchHandle {
  meta?: RouteMeta;
}

/**
 * Emits per-route `<title>`, description, canonical, Open Graph / Twitter card
 * tags, and JSON-LD structured data into the document head. Rendered once
 * inside the router so it runs during both SSG (pre-render) and client
 * hydration — replacing the old effect-only `usePageMeta`, which never
 * populated the static HTML crawlers (and AI answer engines) see first.
 *
 * Metadata comes from the deepest matched route's `handle.meta`
 * (see routes.tsx); i18n keys are translated here so the string tracks the
 * active language, with literal fallbacks for the not-yet-translated pages.
 */
export function SeoHead() {
  const { t } = useTranslation('marketing');
  const matches = useMatches();
  const { pathname } = useLocation();

  const metaMatch = [...matches].reverse().find((m) => (m.handle as MatchHandle | undefined)?.meta);
  const routeMeta = (metaMatch?.handle as MatchHandle | undefined)?.meta;

  const rawTitle = routeMeta?.titleKey ? t(routeMeta.titleKey) : routeMeta?.title;
  const description = routeMeta?.descriptionKey ? t(routeMeta.descriptionKey) : routeMeta?.description;
  const title = rawTitle ? `${rawTitle} · ${SITE_NAME}` : SITE_NAME;

  // Canonical is the path without a trailing slash (apex root stays "/").
  const canonicalPath = pathname !== '/' && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
  const canonical = `${CANONICAL_ORIGIN}${canonicalPath}`;

  // Always-on graph (Organization + WebSite + SoftwareApplication); the FAQ
  // page additionally emits an FAQPage node built from the same i18n copy.
  const graph = baseGraph();
  if (pathname === '/help/faq') {
    graph.push(faqGraph(t));
  }

  return (
    <Head>
      <title>{title}</title>
      {description ? <meta name="description" content={description} /> : null}
      <link rel="canonical" href={canonical} />

      {/* Open Graph */}
      <meta property="og:title" content={title} />
      {description ? <meta property="og:description" content={description} /> : null}
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={OG_IMAGE} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="WorkingClock — time tracking across web, mobile, and desktop" />

      {/* Twitter / X card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      {description ? <meta name="twitter:description" content={description} /> : null}
      <meta name="twitter:image" content={OG_IMAGE} />

      {/* JSON-LD structured data for search + AI answer engines */}
      <script type="application/ld+json">{jsonLd(graph)}</script>
    </Head>
  );
}
